import React, { useState, useCallback } from 'react';
import { Box, Fab, Zoom, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import FolderIcon from '@mui/icons-material/Folder';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CasinoIcon from '@mui/icons-material/Casino';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import { androidInset } from '../utils/platformUtils';
import UnreadDot from './UnreadDot';
import Tip, { coachBlinkSx } from './Tip';
import PlaybackMixer from './PlaybackMixer';

/**
 * RadialFabMenu
 * Phone-landscape floating action button that expands to a radial (fan) menu.
 * Matches portrait bottom nav: Play, Jam, You, Presets, Random dice.
 */
export default function RadialFabMenu({
  activePanelId,
  setActivePanelId,
  currentPage,
  setCurrentPage,
  setTabValue,
  onGoToPlay,
  onGoToPractice,
  onRandomSelection,
  unreadVisible = false,
  coachStep = null,
  onCoachJamTap,
  onCoachTipDismiss,
}) {
  const [open, setOpen] = useState(false);

  const togglePanel = useCallback((id) => {
    if (!setActivePanelId) return;
    setActivePanelId(prev => (prev === id ? null : id));
    setOpen(false);
  }, [setActivePanelId]);

  // Play et Practice passent par les gestes de App : pendant une lecon, ils la
  // referment avant de changer de page. Les autres entrees posent la page.
  const navigateTo = useCallback((page, tabIndex) => {
    if (page === 'home' && onGoToPlay) onGoToPlay();
    else if (page === 'learn' && onGoToPractice) onGoToPractice();
    else {
      if (setCurrentPage) setCurrentPage(page);
      if (setTabValue) setTabValue(tabIndex);
    }
    if (setActivePanelId) setActivePanelId(null);
    setOpen(false);
  }, [setCurrentPage, setTabValue, setActivePanelId, onGoToPlay, onGoToPractice]);

  const handleRandom = useCallback(() => {
    if (onRandomSelection) onRandomSelection();
    setOpen(false);
  }, [onRandomSelection]);

  const actions = [
    { id: 'home', label: 'Play', icon: <MusicNoteIcon />, onClick: () => navigateTo('home', 0), isPage: true },
    { id: 'learn', label: 'Practice', icon: <TipsAndUpdatesIcon />, onClick: () => { if (coachStep === 'tapJam' && onCoachJamTap) { setOpen(false); onCoachJamTap(); return; } navigateTo('learn', 1); }, isPage: true },
    { id: 'settings', label: 'You', icon: <PersonOutlineOutlinedIcon />, onClick: () => navigateTo('settings', 2), isPage: true, unread: unreadVisible },
    // Presets hidden for now — to be reintroduced as a feature update
    // { id: 'patterns', label: 'Presets', icon: <FolderIcon />, onClick: () => togglePanel('patterns') },
    { id: 'progression', label: 'Progression', icon: <QueueMusicIcon />, onClick: () => togglePanel('progression') },
    { id: 'random', label: 'Random', icon: <CasinoIcon />, onClick: handleRandom },
    { id: 'mixer', label: 'Mixer' },
  ];

  // Layout: two-ring fan from bottom-right; items arc inward and upward
  const outerRadius = 120; // px distance from center for outer ring
  const innerRadius = 63;  // px distance from center for inner ring
  const angleStart = 100;  // top-ish
  const angleEnd = 2;     // keep within screen

  return (
    <Box
      className="radial-fab-menu"
      sx={{
        position: 'fixed',
        // Le FAB reste seul a l'ecran en paysage compact, ou la barre de
        // navigation Android passe sur le cote : les deux insets comptent.
        right: `calc(5px + ${androidInset('right')})`,
        bottom: `calc(5px + ${androidInset('bottom')})`,
        zIndex: 1400,
      }}
    >
      {/* Circular backdrop overlay when open */}
      {open && (() => {
        const fabSize = 56; // default MUI Fab size
        const overlayRadius = outerRadius + 60; // slightly reduced from +80
        const diameter = overlayRadius * 2;
        return (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              right: `${-(overlayRadius - fabSize / 2)}px`,
              bottom: `${-(overlayRadius - fabSize / 2)}px`,
              width: `${diameter}px`,
              height: `${diameter}px`,
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.60)',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        );
      })()}
      {/* Radial action buttons - two rings */}
      {(() => {
        const outerActions = actions.slice(0, 4);
        const innerActions = actions.slice(4);

        const renderRing = (ringActions, radius, baseDelay = 200, opts = {}) => {
          const { customStart, customEnd, centerAngle, spread } = opts;
          const useCenterMode = typeof centerAngle === 'number' && typeof spread === 'number';
          const denom = Math.max(ringActions.length - 1, 1);
          return ringActions.map((action, i) => {
            // Distribute angles
            let angleDegRaw;
            if (useCenterMode) {
              // Place items evenly within a narrow spread around a center angle
              const startA = centerAngle + spread / 2;
              const endA = centerAngle - spread / 2;
              angleDegRaw = startA - (i * ((startA - endA) / denom));
            } else {
              const startA = typeof customStart === 'number' ? customStart : angleStart;
              const endA = typeof customEnd === 'number' ? customEnd : angleEnd;
              angleDegRaw = startA - (i * ((startA - endA) / denom));
            }
            const angleDeg = Math.max(angleEnd, Math.min(angleStart, angleDegRaw));
            const angleRad = (Math.PI / 180) * angleDeg;
            const x = Math.cos(angleRad) * radius;
            const y = Math.sin(angleRad) * radius;
            const isActive = action.isPage ? currentPage === action.id : activePanelId === action.id;
            return (
              <Zoom in={open} timeout={baseDelay + i * 40} unmountOnExit key={`${action.id}-${radius}`}>
                <Box
                  sx={{ position: 'absolute', right: `${x + 30}px`, bottom: `${y + 4}px` }}
                >
                  {action.id === 'mixer' ? (
                    <PlaybackMixer buttonSx={{
                      minWidth: 48, width: 48, height: 48,
                      bgcolor: 'rgba(0,0,0,0.8)',
                      borderColor: 'rgba(255,255,255,0.2)',
                      '&:hover': { bgcolor: 'rgba(30,30,47,0.9)' },
                    }} />
                  ) : (
                    <IconButton
                      size="large"
                      aria-label={action.label}
                      onClick={action.onClick}
                      sx={{
                        color: isActive ? 'success.main' : 'white',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        '&:hover': { backgroundColor: 'rgba(30,30,47,0.9)' },
                      }}
                    >
                      {action.icon}
                    </IconButton>
                  )}
                  <UnreadDot visible={!!action.unread} sx={{ top: 4, right: 4 }} />
                </Box>
              </Zoom>
            );
          });
        };

        return (
          <>
            {renderRing(outerActions, outerRadius, 200)}
            {renderRing(innerActions, innerRadius, 260, { centerAngle: 52, spread: 60 })}
          </>
        );
      })()}

      {/* Main FAB */}
      {coachStep === 'tapJam' && (
        <Tip text="Tap to practice" align="end" duration={8000} onDismiss={onCoachTipDismiss} />
      )}
      <Fab
        onClick={() => {
          // En paysage compact il n'y a pas de barre du bas : c'est le FAB qui
          // porte l'invitation, et pendant le tip il lance le backing direct.
          if (coachStep === 'tapJam' && onCoachJamTap) {
            onCoachJamTap();
            return;
          }
          setOpen(v => !v);
        }}
        sx={{
          color: 'white',
          backgroundColor: open ? 'rgba(244, 67, 54, 0.92)' : 'transparent',
          border: open ? 'none' : '1px solid rgba(255,255,255,0.12)',
          '&:hover': {
            backgroundColor: open ? 'rgba(244, 67, 54, 1)' : 'rgba(255, 255, 255, 0.1)'
          },
          boxShadow: open ? '0 10px 24px rgba(0,0,0,0.45)' : 'none',
          ...(coachStep === 'tapJam' && coachBlinkSx)
        }}
        aria-label={open ? 'Close menu' : 'Open menu'}
      >
        {open ? <CloseIcon /> : <MenuIcon />}
      </Fab>
      <UnreadDot visible={!open && unreadVisible} sx={{ top: 6, right: 6 }} />
    </Box>
  );
}
